import Decimal from 'decimal.js-light';
import type { InstrumentDto, InstrumentKind, SlipExtraction } from '../shared/types';
import { thbPerUsdForForm } from './fx';

const GRAMS_PER_TROY_OUNCE = new Decimal('31.1034768');

export function grossAmountLabel(
  slipType: SlipExtraction['slipType'],
  instrumentKind?: InstrumentKind | null,
) {
  if (
    slipType === 'TH_STOCK'
    || slipType === 'US_STOCK'
    || instrumentKind === 'TH_EQUITY'
    || instrumentKind === 'US_EQUITY'
    || instrumentKind === 'ETF'
  ) return 'มูลค่าหุ้น';
  if (slipType === 'GOLD' || instrumentKind === 'GOLD') return 'มูลค่าทองคำ';
  return 'มูลค่ารวม';
}

export type ReviewAccount = { id: string; name: string; type: string; defaultCurrency: string };

export type InstrumentCreateInput = {
  symbol: string;
  name: string;
  kind: InstrumentKind;
  market: string;
  quoteCurrency: 'USD' | 'THB';
  unit: string;
  providerSymbol: string;
  coingeckoId: string;
};

export function reviewAmountFieldVisibility(eventType: string) {
  const isFx = eventType === 'FX_EXCHANGE';
  return { showFxAmounts: isFx, showAssetAmounts: !isFx };
}

export function hasIncompleteFxReview(form: Record<string, string>) {
  return form.eventType === 'FX_EXCHANGE'
    && (!form.relatedAccountId || !form.counterInstrumentId || !form.cashAmount || !form.counterAmount);
}

function transformDecimal(value: string, transform: (decimal: Decimal) => Decimal) {
  if (!value) return value;
  try {
    return transform(new Decimal(value)).toFixed();
  } catch {
    return value;
  }
}

export function buildReviewForm({
  extraction,
  accounts,
  instruments,
  usdThb,
  fallbackOccurredAt,
}: {
  extraction: Partial<SlipExtraction>;
  accounts: ReviewAccount[];
  instruments: InstrumentDto[];
  usdThb: string;
  fallbackOccurredAt: string;
}) {
  const eventType = extraction.eventType || 'BUY';
  const isFx = eventType === 'FX_EXCHANGE';
  const sourceCurrency = extraction.sourceCurrency || extraction.fundingCurrency || extraction.tradeCurrency || 'USD';
  const destinationCurrency = extraction.destinationCurrency || (sourceCurrency === 'THB' ? 'USD' : 'THB');
  const isUsdAssetSlip = extraction.slipType === 'US_STOCK' || extraction.slipType === 'GOLD';
  const explicitlyThbFunded = eventType === 'BUY'
    && isUsdAssetSlip
    && extraction.sourceCurrency === 'THB';
  const legacyThbFunded = eventType === 'BUY'
    && isUsdAssetSlip
    && extraction.tradeCurrency === 'THB'
    && extraction.fundingCurrency === 'USD';
  const isThbFundedUsdAsset = explicitlyThbFunded || legacyThbFunded;
  const tradeCurrency = isFx ? sourceCurrency
    : isUsdAssetSlip ? 'USD'
      : extraction.tradeCurrency || 'USD';
  const cashCurrency = isFx ? sourceCurrency : isThbFundedUsdAsset ? 'THB' : tradeCurrency;
  const sourceInstrument = instruments.find((item) => item.kind === 'FIAT' && item.symbol === sourceCurrency);
  const destinationInstrument = instruments.find((item) => item.kind === 'FIAT' && item.symbol === destinationCurrency);
  const matchingInstrument = instruments.find((item) =>
    item.symbol.toUpperCase() === String(extraction.symbol || '').toUpperCase()
    || (extraction.slipType === 'GOLD' && item.kind === 'GOLD'));
  const defaultAccount = extraction.slipType === 'GOLD'
    ? accounts.find((item) => item.type === 'GOLD')
    : isFx ? accounts.find((item) => item.defaultCurrency === sourceCurrency && item.name.includes('Save'))
      : accounts.find((item) => item.name.includes(extraction.market === 'SET' ? 'หุ้นไทย' : 'หุ้นสหรัฐ'));
  const relatedAccount = isFx
    ? accounts.find((item) => item.defaultCurrency === destinationCurrency && item.name.includes('FCD'))
    : null;
  const goldInGrams = extraction.slipType === 'GOLD' && /^(g|gram|grams)$/i.test((extraction.unit || '').trim());
  const quantity = goldInGrams && extraction.quantity
    ? transformDecimal(extraction.quantity, (value) => value.div(GRAMS_PER_TROY_OUNCE))
    : extraction.quantity || '';
  const unitPrice = goldInGrams && extraction.unitPrice
    ? transformDecimal(extraction.unitPrice, (value) => value.mul(GRAMS_PER_TROY_OUNCE))
    : extraction.unitPrice || '';
  const noteParts = [
    extraction.orderType ? `Order: ${extraction.orderType}` : '',
    extraction.paymentRef ? `Payment ref: ${extraction.paymentRef}` : '',
    extraction.cashbackAmount ? `Cashback ${extraction.cashbackAmount} ${extraction.cashbackCurrency || ''}` : '',
  ].filter(Boolean);

  return {
    accountId: defaultAccount?.id || accounts[0]?.id || '',
    relatedAccountId: relatedAccount?.id || '',
    eventType,
    occurredAt: extraction.occurredAt ? extraction.occurredAt.slice(0, 16) : fallbackOccurredAt,
    instrumentId: isFx ? sourceInstrument?.id || '' : matchingInstrument?.id || '',
    counterInstrumentId: isFx ? destinationInstrument?.id || '' : '',
    quantity,
    counterAmount: isFx
      ? extraction.destinationAmount || ''
      : isThbFundedUsdAsset
        ? extraction.destinationAmount || extraction.fundingAmount || ''
        : '',
    unitPrice,
    grossAmount: legacyThbFunded
      ? extraction.destinationAmount || extraction.fundingAmount || ''
      : extraction.grossAmount || '',
    fee: extraction.fee || '0',
    tax: extraction.tax || '0',
    discountAmount: extraction.discountAmount || '0',
    discountCurrency: extraction.discountCurrency || cashCurrency,
    cashAmount: isFx
      ? extraction.sourceAmount || extraction.fundingAmount || ''
      : isThbFundedUsdAsset
        ? extraction.sourceAmount || extraction.netAmount || extraction.grossAmount || ''
        : extraction.netAmount || extraction.grossAmount || '',
    tradeCurrency,
    cashCurrency,
    thbPerUsd: tradeCurrency === 'THB' || cashCurrency === 'THB'
      ? thbPerUsdForForm(extraction.exchangeRate, usdThb)
      : '',
    externalRef: extraction.externalRef || '',
    notes: noteParts.join(' · '),
  };
}
